import { DateRange } from '@shared/components/chitin/molecules/daterange-picker/date-range';
import { dateRangePresets } from '@shared/components/chitin/molecules/daterange-picker/date-range-presets';
import { NumberRange } from '@shared/components/chitin/molecules/range-input/range-input.component';
import { Filter } from '@shared/components/data-view/data-view-types';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';

export type MonthDay = {
  month: number;
  day?: number;
};

export function updateFilterWithDisplayValue(filter: Filter.AppliedDataViewFilter) {
  filter.icon = undefined;
  filter.iconText = undefined;
  filter.iconTextPostfix = undefined;
  filter.displayValue = undefined;
  if (!(filter.value == undefined || filter.value === '')) {
    switch (filter.type) {
      case 'beginsWith': {
        filter.icon = 'fa-solid fa-equals';
        filter.iconText = '...';
        filter.iconTextPostfix = true;
        break;
      }
      case 'endsWith': {
        filter.icon = 'fa-solid fa-equals';
        filter.iconText = '...';
        break;
      }
      case 'contains': {
        filter.icon = 'fa-solid fa-tilde';
        break;
      }
      case 'ncontains': {
        filter.icon = 'fa-solid fa-object-exclude';
        break;
      }
      case 'date': {
        const valueDate = moment(filter.value);
        filter.displayValue = valueDate.format('MM/DD/YYYY');
        filter.icon = 'fa-solid fa-calendar';
        break;
      }
      case 'dateRange':
      case 'datetimeRange': {
        const valueDateRange = filter.value as DateRange;
        const preset = valueDateRange.preset;
        if (preset) {
          filter.displayValue = valueDateRange.presetDisplayValue ?? preset;
          filter.icon = 'fa-solid fa-calendar';
          if (!valueDateRange.from || !valueDateRange.to) {
            const dateRangeValue = dateRangePresets()[preset];
            if (!dateRangeValue) {
              console.warn(`Unknown date range preset: ${preset}`);
              break;
            }
            filter.value = dateRangeValue;
            filter.displayValue =
              getDisplayValueForDateRangePreset(dayjs(dateRangeValue.from), dayjs(dateRangeValue.to), preset) ?? filter.displayValue;
          }
        } else {
          const from = moment(valueDateRange.from).format('MM/DD/YYYY');
          const to = moment(valueDateRange.to).format('MM/DD/YYYY');
          if (from === to) {
            filter.displayValue = from;
            filter.icon = 'fa-solid fa-calendar';
            break;
          }
          filter.icon = 'fa-solid fa-calendar-clock';
          filter.displayValue = `${from} - ${to}`;
        }
        if (filter.timezone) {
          filter.displayValue += ` (${filter.timezone})`;
        }
        break;
      }
      case 'monthDaySelect': {
        const monthDay = filter.value as MonthDay;
        if (monthDay.day === undefined) {
          filter.displayValue = moment().set('month', monthDay.month).format('MMMM');
          filter.icon = 'fa-solid fa-calendar';
        } else {
          filter.icon = 'fa-solid fa-calendar-clock';
          filter.displayValue = moment()
            .set('month', monthDay.month)
            .set('date', monthDay.day + 1)
            .format('MM/DD');
        }
        break;
      }
      case 'equal': {
        filter.icon = 'fa-solid fa-equals';
        const filterStringValue = `${filter.value}`;
        if (filterStringValue.length > 12) {
          filter.displayValue = `${filterStringValue.slice(0, 3)}...${filterStringValue.slice(-6)}`;
        }
        break;
      }
      case 'nequal': {
        filter.icon = 'fa-solid fa-not-equal';
        break;
      }
      case 'range': {
        const from = (filter.value as NumberRange).from;
        const to = (filter.value as NumberRange).to;
        if (!from && !to) {
          filter.displayValue = '';
          break;
        }
        if (!from) {
          filter.icon = 'fa-solid fa-less-than-equal';
          filter.displayValue = `${to}`;
          break;
        }
        if (!to) {
          filter.icon = 'fa-solid fa-greater-than-equal';
          filter.displayValue = `${from}`;
          break;
        }
        filter.iconText = 'IN RANGE';
        filter.displayValue = `${from} - ${to}`;
        break;
      }
      case 'lt': {
        filter.icon = 'fa-solid fa-less-than';
        break;
      }
      case 'gt': {
        filter.icon = 'fa-solid fa-greater-than';
        break;
      }
      case 'in': {
        if (Array.isArray(filter.value)) {
          if ((filter.options ?? []).length > 0 && filter.value.length === 1) {
            filter.icon = 'fa-solid fa-equals';
            filter.displayValue = filter.options!.find(option => option.value === (filter.value as string[])[0])?.name ?? '';
          } else {
            filter.iconText = 'IN';
            filter.displayValue = `[${filter.value.length} options]`;
          }
        } else {
          filter.icon = 'fa-solid fa-equals';
          filter.displayValue = filter.options!.find(option => option.value === filter.value)?.name ?? '';
        }
        break;
      }
      case 'nin': {
        filter.iconText = 'NOT IN';
        filter.displayValue = Array.isArray(filter.value)
          ? `[${filter.value.length} option${filter.value.length === 1 ? '' : 's'}]`
          : undefined;
        break;
      }
      case 'isOneOf': {
        const typedValue = filter.value as string[];
        filter.iconText = 'IS ONE OF';
        filter.displayValue = typedValue.length > 0 ? `[${typedValue.length} option${typedValue.length === 1 ? '' : 's'}]` : '';
        break;
      }
      case 'nisOneOf': {
        const typedValue = filter.value as string[];
        filter.iconText = "ISN'T ONE OF";
        filter.displayValue = typedValue.length > 0 ? `[${typedValue.length} option${typedValue.length === 1 ? '' : 's'}]` : '';
        break;
      }
      case 'exists': {
        filter.iconText = 'EXISTS';
        filter.displayValue = '';
        break;
      }
      case 'nexists': {
        filter.iconText = "DOESN'T EXIST";
        filter.displayValue = '';
        break;
      }
    }
  }
}

export function getDisplayValueForDateRangePreset(
  startDate: Dayjs | null,
  endDate: Dayjs | null,
  preset: string | null,
  dateFormat: string | null = null,
): string | null {
  let date = '';
  if (!startDate || !endDate) return preset;

  if (dateFormat !== null) {
    if (startDate.isSame(endDate, 'day')) {
      date = `${startDate.format(dateFormat)}`;
    } else {
      date = `${startDate.format(dateFormat)} - ${endDate.format(dateFormat)}`;
    }
  } else {
    const isSameDay = startDate.get('date') === endDate.get('date');
    const isSameMonth = startDate.get('month') === endDate.get('month');
    const isSameYear = startDate.get('year') === endDate.get('year');

    if (isSameDay && isSameMonth && isSameYear) {
      date = `${startDate.format('MM')}/${endDate.format('DD')}`;
    } else if (!isSameDay && isSameYear) {
      date = `${startDate.format('MM')}/${startDate.format('DD')} - ${endDate.format('MM')}/${endDate.format('DD')}`;
    } else {
      date = `${startDate.format('MM')}/${startDate.format('DD')}/${startDate.format('YYYY')} - ${endDate.format('MM')}/${endDate.format(
        'DD',
      )}/${endDate.format('YYYY')}`;
    }
  }

  return preset === null ? date : `${preset} (${date})`;
}
